import React, { useEffect, useState, useRef } from 'react';
import { Box, Container, Divider, Typography } from '@mui/material';
import { useParams } from 'react-router-dom';
import { useDossieService } from '../../../../use-cases/dossie';
import type { IDossiePFResponse, IDossiePJResponse } from '../../../../application/models/dossie';
import {
  DossieHeader,
  DossieLoading,
  DossieError,
  InformacoesPrincipaisCard,
  LocalizacaoContatosCard,
  AnaliseCreditoCard,
  SituacaoCadastralCard,
  RegistroObitoCard,
  QuadroSocietarioCard,
  ParticipacaoEmpresarialCard,
  OrigemConsultaWidget,
  formatDocument,
} from './components';
import './styles/dossie-print.css';

export const DossieResultsPage: React.FC = () => {
  const { documento } = useParams<{ documento: string }>();
  const { fetchDossie, currentResult, loading, error: apiError } = useDossieService();
  const [data, setData] = useState<IDossiePFResponse | IDossiePJResponse | null>(currentResult);

  const rawDocument = documento?.replace(/[^A-Za-z0-9]/g, '') || '';
  const isAlphanumeric = /[A-Z]/i.test(rawDocument);
  const isPF = !isAlphanumeric && rawDocument.length <= 11;
  const docFormatado = formatDocument(rawDocument, isPF);

  const fetchedRef = useRef<string | null>(null);

  useEffect(() => {
    if (currentResult) {
      setData(currentResult);
    }
  }, [currentResult]);

  useEffect(() => {
    if (documento && fetchedRef.current !== documento) {
      fetchedRef.current = documento;
      fetchDossie(documento)
        .then((res: any) => {
          setData(res);
        })
        .catch(() => { });
    }
  }, [documento, fetchDossie]);

  if (loading || (!data && !apiError)) {
    return <DossieLoading />;
  }

  if (apiError) {
    return <DossieError message={apiError} />;
  }

  const pfData = isPF ? (data as IDossiePFResponse) : null;
  const pjData = !isPF ? (data as IDossiePJResponse) : null;

  return (
    <Box className="print-page-wrapper" sx={{ width: '100%', flexGrow: 1, minHeight: '100vh', backgroundColor: '#f5f3f7', paddingY: { xs: 2.5, md: 4 } }}>
      <Container className="print-container" maxWidth="xl" sx={{ maxWidth: { xs: '100%', xl: '1560px' }, px: { xs: 2, sm: 3, md: 4 } }}>
        <Box
          className="print-main-card"
          sx={{
            backgroundColor: '#ffffff',
            borderRadius: '24px',
            boxShadow: '0 12px 40px rgba(0,0,0,0.03)',
            border: '1px solid #ece7f2',
            overflow: 'hidden'
          }}
        >
          {/* Header da Consulta */}
          <DossieHeader isPF={isPF} docFormatado={docFormatado} />

          <Divider sx={{ mx: { xs: 2.5, md: 4 }, borderColor: '#f0ebf5' }} />

          {/* Cards de Resultados */}
          <Box className="print-content-area" p={{ xs: 2, sm: 3, md: 4 }}>
            {isPF ? (
              <>
                {/* 1. Identificação de Pessoa Física */}
                <InformacoesPrincipaisCard
                  localizacao={data?.localizacao ?? null}
                  isPF={isPF}
                  docFormatado={docFormatado}
                />

                {/* 2. Localização e Contatos */}
                <LocalizacaoContatosCard localizacao={data?.localizacao ?? null} />

                {/* 3. Análise de Crédito (Serasa Score) */}
                <AnaliseCreditoCard serasaScore={data?.serasaScore ?? null} />

                {/* 4. Registro de Óbito (Exclusivo PF) */}
                <RegistroObitoCard obito={pfData?.obito ?? null} />

                {/* 5. Participação Empresarial */}
                <ParticipacaoEmpresarialCard
                  participacaoEmpresarial={data?.participacaoEmpresarial ?? null}
                />

                {/* 6. Situação Cadastral (Receita Federal) */}
                <SituacaoCadastralCard
                  situacaoCadastral={data?.situacaoCadastral ?? null}
                  isPF={isPF}
                  documento={rawDocument}
                  dataNascimento={pfData?.localizacao?.dadosCadastrais?.DataNascimento}
                />
              </>
            ) : (
              <>
                {/* 1. Identificação de Pessoa Jurídica */}
                <InformacoesPrincipaisCard
                  localizacao={data?.localizacao ?? null}
                  isPF={isPF}
                  docFormatado={docFormatado}
                />

                {/* 2. Quadro Societário */}
                <QuadroSocietarioCard socios={pjData?.socios ?? null} />

                {/* 3. Participação Empresarial */}
                <ParticipacaoEmpresarialCard
                  participacaoEmpresarial={data?.participacaoEmpresarial ?? null}
                />

                {/* 4. Localização e Contatos */}
                <LocalizacaoContatosCard localizacao={data?.localizacao ?? null} />

                {/* 5. Análise de Crédito (Serasa Score) */}
                <AnaliseCreditoCard serasaScore={data?.serasaScore ?? null} />

                {/* 6. Situação na Receita Federal */}
                <SituacaoCadastralCard
                  situacaoCadastral={data?.situacaoCadastral ?? null}
                  isPF={isPF}
                  documento={rawDocument}
                  dataAbertura={
                    pjData?.localizacao?.dadosCadastrais?.DataAbertura ||
                    (pjData?.situacaoCadastral as any)?.dadosEmpresa?.DataAbertura
                  }
                />
              </>
            )}

            {/* Minuta de Declaração LGPD */}
            <Box
              className="lgpd-declaracao-box avoid-break"
              sx={{
                mt: { xs: 3, md: 3.5 },
                p: { xs: 2, sm: 2.5 },
                backgroundColor: '#faf8fc',
                borderRadius: '14px',
                border: '1px solid #e9dff0',
                borderLeft: '4px solid #723989',
                display: 'flex',
                flexDirection: 'column',
                gap: 0.75,
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 800,
                  color: '#723989',
                  fontSize: { xs: '0.8rem', sm: '0.85rem' },
                  letterSpacing: '0.04em',
                  textTransform: 'uppercase',
                }}
              >
                MINUTA DE DECLARAÇÃO LGPD
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: '#475569',
                  fontSize: { xs: '0.75rem', sm: '0.8rem' },
                  lineHeight: 1.6,
                  fontWeight: 600,
                }}
              >
                DECLARO QUE A UTILIZAÇÃO DOS DADOS RESPEITARÁ AS FINALIDADES E PROCEDIMENTOS LEGALMENTE ADMITIDOS PELA LEI GERAL DE PROTEÇÃO DE DADOS DIVULGADOS EM MINHA PÁGINA INICIAL DE ACESSO.
              </Typography>
            </Box>
          </Box>

        </Box>
      </Container>
      <OrigemConsultaWidget />
    </Box>
  );
};
